import { Badge, Box, Card, Flex, Icon, IconButton, Stack, Text } from '@chakra-ui/react';
import { CheckIcon, EyeIcon } from '@heroicons/react/24/outline';
import DOMPurify from 'dompurify';
import { Prose } from '@/components/ui/prose';
import { PlanItem, PlannerPlan } from '@/lib/types';
import { computeSectionStartTimes, formatTime12h } from '@/pages/planner/draft';
import { slotFor } from '@/lib/slot-color';

// Picked from PlannerPlan (the day plan), which is the only entity carrying all
// five fields. A CurriculumPlan slot has no learning_outcomes/prerequisites —
// those are declared once on the parent Curriculum — so curriculum-slot callers
// merge the curriculum's values onto the slot before passing it in (see
// cur-slot-view-screen.tsx) rather than widening this type.
type PlanBodyPlan = Pick<
  PlannerPlan,
  'learning_outcomes' | 'prerequisites' | 'goals' | 'notes' | 'sections'
>;

// Goals/Notes are authored in the builder's rich text editor and stored as
// HTML. Sanitize before injecting (this component also backs the public plan
// page). whiteSpace pre-wrap keeps line breaks in legacy plain-text plans
// saved before the editor switch; CKEditor output is compact HTML with no
// stray newlines, so it's a no-op for new content. The css prop trims Prose's
// first/last paragraph margins so spacing under the eyebrow stays unchanged.
function RichText({ html }: { html: string }) {
  return (
    <Prose
      maxWidth='100%'
      whiteSpace='pre-wrap'
      css={{ '& > :first-child': { marginTop: 0 }, '& > :last-child': { marginBottom: 0 } }}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
}

interface PlanBodyProps {
  plan: PlanBodyPlan;
  // Day plans have a start_time ('HH:mm') and show a computed start-time chip
  // per section. Curriculum slots have no start_time in the wire contract —
  // omit to render durations only (curSlotView's usage).
  startTime?: string;
  onViewItem: (item: PlanItem) => void;
}

// Shared read-only plan body for the view screens: Learning outcomes and
// Prerequisites highlight boxes, Goals/Notes eyebrow sections, and the
// sections list with item rows (slot-colored content-type badge + detail
// eye button). Used by the day-plan view (view-screen.tsx) and the
// curriculum-slot view (cur-slot-view-screen.tsx); the header, meta chips,
// actions, and item drawer stay screen-specific.
export default function PlanBody({ plan, startTime, onViewItem }: PlanBodyProps) {
  const sectionStartTimes = startTime ? computeSectionStartTimes(startTime, plan.sections) : [];

  return (
    <>
      {plan.learning_outcomes && (
        <Box
          bg='brand.subtle'
          borderRadius='md'
          borderLeftWidth='4px'
          borderLeftColor='brand.solid'
          p={3}
          mb={3}
        >
          <Flex align='center' gap={1.5} mb={1}>
            <Icon as={CheckIcon} w={3.5} h={3.5} color='brand.solid' />
            <Text textStyle='eyebrow'>Learning outcomes</Text>
          </Flex>
          <Text fontSize='sm' color='fg' whiteSpace='pre-wrap'>
            {plan.learning_outcomes}
          </Text>
        </Box>
      )}

      {plan.prerequisites && (
        <Box bg='bg.subtle' borderRadius='md' p={3} mb={3}>
          <Text textStyle='eyebrow' mb={1}>
            Prerequisites
          </Text>
          <Text fontSize='sm' color='fg' whiteSpace='pre-wrap'>
            {plan.prerequisites}
          </Text>
        </Box>
      )}

      {plan.goals && (
        <Box mb={3}>
          <Text textStyle='eyebrow' mb={1}>
            Goals & objectives
          </Text>
          <RichText html={plan.goals} />
        </Box>
      )}

      {plan.notes && (
        <Box mb={5}>
          <Text textStyle='eyebrow' mb={1}>
            Notes
          </Text>
          <RichText html={plan.notes} />
        </Box>
      )}

      <Text textStyle='eyebrow' mb={3}>
        Sections
      </Text>
      <Stack gap={3}>
        {plan.sections.length === 0 && (
          <Text fontSize='sm' color='fg.subtle'>
            No sections in this plan.
          </Text>
        )}
        {plan.sections.map((section) => {
          const sectionStart = sectionStartTimes.find((s) => s.sectionUid === section.uid)?.startTime;
          return (
            <Card.Root key={section.uid}>
              <Card.Body>
                <Flex align='center' justify='space-between' mb={2}>
                  <Text fontWeight='600' color='fg.strong'>
                    {section.name}
                  </Text>
                  <Text fontSize='xs' color='fg.muted'>
                    {sectionStart && `${formatTime12h(sectionStart)} · `}
                    {section.duration_minutes} min
                  </Text>
                </Flex>
                {section.notes && (
                  <Text fontSize='sm' color='fg.muted' mb={2} whiteSpace='pre-wrap'>
                    {section.notes}
                  </Text>
                )}
                <Stack gap={1}>
                  {section.items.map((item) => {
                    const slot = item.content_id != null ? slotFor(String(item.content_type_id)) : null;
                    const label = item.content_type_name ?? item.custom_label;
                    return (
                      <Flex key={item.uid} align='center' gap={2}>
                        <Text fontSize='sm' color='fg' flex='1'>
                          {item.name}
                        </Text>
                        {label && (
                          <Badge
                            size='sm'
                            bg={slot ? `slot${slot}.bg` : 'bg.subtle'}
                            color={slot ? `slot${slot}.fg` : 'fg.muted'}
                          >
                            {label}
                          </Badge>
                        )}
                        <IconButton
                          aria-label='View detail'
                          variant='ghost'
                          size='xs'
                          className='no-print'
                          onClick={() => onViewItem(item)}
                        >
                          <Icon as={EyeIcon} w={4} h={4} />
                        </IconButton>
                      </Flex>
                    );
                  })}
                  {section.items.length === 0 && (
                    <Text fontSize='xs' color='fg.subtle'>
                      No items in this section.
                    </Text>
                  )}
                </Stack>
              </Card.Body>
            </Card.Root>
          );
        })}
      </Stack>
    </>
  );
}
